import * as Cesium from "cesium";

// ImprovedNoise from Three.js
// https://github.com/mrdoob/three.js/blob/dev/examples/jsm/math/ImprovedNoise.js

const lerp = Cesium.Math.lerp;

const _p = [
  151, 160, 137, 91, 90, 15, 131, 13, 201, 95, 96, 53, 194, 233, 7, 225, 140,
  36, 103, 30, 69, 142, 8, 99, 37, 240, 21, 10, 23, 190, 6, 148, 247, 120, 234,
  75, 0, 26, 197, 62, 94, 252, 219, 203, 117, 35, 11, 32, 57, 177, 33, 88, 237,
  149, 56, 87, 174, 20, 125, 136, 171, 168, 68, 175, 74, 165, 71, 134, 139, 48,
  27, 166, 77, 146, 158, 231, 83, 111, 229, 122, 60, 211, 133, 230, 220, 105,
  92, 41, 55, 46, 245, 40, 244, 102, 143, 54, 65, 25, 63, 161, 1, 216, 80, 73,
  209, 76, 132, 187, 208, 89, 18, 169, 200, 196, 135, 130, 116, 188, 159, 86,
  164, 100, 109, 198, 173, 186, 3, 64, 52, 217, 226, 250, 124, 123, 5, 202, 38,
  147, 118, 126, 255, 82, 85, 212, 207, 206, 59, 227, 47, 16, 58, 17, 182, 189,
  28, 42, 223, 183, 170, 213, 119, 248, 152, 2, 44, 154, 163, 70, 221, 153, 101,
  155, 167, 43, 172, 9, 129, 22, 39, 253, 19, 98, 108, 110, 79, 113, 224, 232,
  178, 185, 112, 104, 218, 246, 97, 228, 251, 34, 242, 193, 238, 210, 144, 12,
  191, 179, 162, 241, 81, 51, 145, 235, 249, 14, 239, 107, 49, 192, 214, 31,
  181, 199, 106, 157, 184, 84, 204, 176, 115, 121, 50, 45, 127, 4, 150, 254,
  138, 236, 205, 93, 222, 114, 67, 29, 24, 72, 243, 141, 128, 195, 78, 66, 215,
  61, 156, 180,
];

for (let i = 0; i < 256; i++) {
  _p[256 + i] = _p[i];
}

function fade(t) {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function grad(hash, x, y, z) {
  const h = hash & 15;
  const u = h < 8 ? x : y,
    v = h < 4 ? y : h === 12 || h === 14 ? x : z;
  return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
}

/**
 * A utility class providing a 3D noise function.
 *
 * The code is based on [IMPROVED NOISE]{@link https://cs.nyu.edu/~perlin/noise/}
 * by Ken Perlin, 2002.
 */
class ImprovedNoise {
  /**
   * Returns a noise value for the given parameters.
   *
   * @param {number} x - The x coordinate.
   * @param {number} y - The y coordinate.
   * @param {number} z - The z coordinate.
   * @return {number} The noise value.
   */
  noise(x, y, z) {
    const floorX = Math.floor(x),
      floorY = Math.floor(y),
      floorZ = Math.floor(z);

    const X = floorX & 255,
      Y = floorY & 255,
      Z = floorZ & 255;

    x -= floorX;
    y -= floorY;
    z -= floorZ;

    const xMinus1 = x - 1,
      yMinus1 = y - 1,
      zMinus1 = z - 1;

    const u = fade(x),
      v = fade(y),
      w = fade(z);

    const A = _p[X] + Y,
      AA = _p[A] + Z,
      AB = _p[A + 1] + Z,
      B = _p[X + 1] + Y,
      BA = _p[B] + Z,
      BB = _p[B + 1] + Z;

    return lerp(
      lerp(
        lerp(grad(_p[AA], x, y, z), grad(_p[BA], xMinus1, y, z), u),
        lerp(grad(_p[AB], x, yMinus1, z), grad(_p[BB], xMinus1, yMinus1, z), u),
        v,
      ),
      lerp(
        lerp(
          grad(_p[AA + 1], x, y, zMinus1),
          grad(_p[BA + 1], xMinus1, y, zMinus1),
          u,
        ),
        lerp(
          grad(_p[AB + 1], x, yMinus1, zMinus1),
          grad(_p[BB + 1], xMinus1, yMinus1, zMinus1),
          u,
        ),
        v,
      ),
      w,
    );
  }
}

// End ImprovedNoise from Three.js

// GLSL noise function for use in shaders
const glslNoiseFunction = `
// GLSL 3D Simplex noise function
// From: https://github.com/ashima/webgl-noise
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

    vec3 i  = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);

    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);

    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;

    i = mod289(i);
    vec4 p = permute(permute(permute(
        i.z + vec4(0.0, i1.z, i2.z, 1.0))
        + i.y + vec4(0.0, i1.y, i2.y, 1.0))
        + i.x + vec4(0.0, i1.x, i2.x, 1.0));

    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;

    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);

    vec4 x = x_ *ns.x + ns.yyyy;
    vec4 y = y_ *ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);

    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);

    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));

    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;

    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);

    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x;
    p1 *= norm.y;
    p2 *= norm.z;
    p3 *= norm.w;

    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}

float fbm(vec3 p) {
    float value = 0.0;
    float amplitude = 0.5;
    float frequency = 1.0;

    for(int i = 0; i < 4; i++) {
        value += amplitude * snoise(p * frequency);
        frequency *= 2.0;
        amplitude *= 0.5;
    }
    return value;
}
`;

// GeometryPrimitive
const {
  Cartesian3,
  destroyObject,
  DrawCommand,
  VertexArray,
  GeometryPipeline,
  Matrix4,
} = Cesium;

/**
 * Custom Primitive for Geometry
 */
class GeometryPrimitive {
  /**
   *
   * @param {*} options
   * @param {*} options.modelMatrix
   * @param {*} options.vertexShaderSource
   * @param {*} options.fragmentShaderSource
   * @param {*} options.uniformMap
   * @param {*} options.renderState
   * @param {*} options.pass
   */
  constructor(geometry, options) {
    this.options = options;
    this.geometry = geometry;
  }

  /**
   *
   * @param {*} frameState
   */
  update(frameState) {
    if (Cesium.defined(this._drawCommand)) {
      frameState.commandList.push(this._drawCommand);
      return;
    }
    if (this.geometry.constructor.createGeometry) {
      this.geometry = this.geometry.constructor.createGeometry(this.geometry);
    }

    const context = frameState.context;

    const attributeLocations = GeometryPipeline.createAttributeLocations(
      this.geometry,
    );
    const vertexArray = VertexArray.fromGeometry({
      context: context,
      geometry: this.geometry,
      attributeLocations,
    });

    // calculate boundingSphere
    const boundingSphere = this.geometry.boundingSphere;
    boundingSphere.center = Matrix4.multiplyByPoint(
      this.options.modelMatrix,
      boundingSphere.center,
      new Cartesian3(),
    );
    boundingSphere.radius = 1000000;

    this._boundingSphereWC = [boundingSphere];

    const shaderProgram = Cesium.ShaderProgram.fromCache({
      context: context,
      attributeLocations,
      vertexShaderSource: this.options.vertexShaderSource,
      fragmentShaderSource: this.options.fragmentShaderSource,
    });

    this._drawCommand = new DrawCommand({
      owner: this,
      boundingVolume: boundingSphere,
      primitiveType: this.geometry.primitiveType,
      vertexArray: vertexArray,
      shaderProgram,
      ...this.options,
    });
  }

  destroy() {
    return destroyObject(this);
  }

  isDestroyed() {
    return false;
  }
}
// GeometryPrimitive end

const vertexShader = /* glsl */ `
      in vec3 position3DHigh;
      in vec3 position3DLow;
      in vec3 normal;
      in vec2 st;
      in float batchId;

      out vec3 vOrigin;
      out vec3 vDirection;

      vec4 translateRelativeToEye(vec3 high, vec3 low) {
          vec3 highDifference = high - czm_encodedCameraPositionMCHigh;
          if(length(highDifference) == 0.0f) {
              highDifference = vec3(0);
          }
          vec3 lowDifference = low - czm_encodedCameraPositionMCLow;
          return vec4(highDifference + lowDifference, 1.0f);
      }

      void main()
      {
          vec4 p = translateRelativeToEye(position3DHigh, position3DLow);

          // Cesium provides camera position in model coordinates
          vOrigin = czm_encodedCameraPositionMCHigh + czm_encodedCameraPositionMCLow;

          // Vertex position in model coordinates
          vec3 modelPosition = position3DHigh + position3DLow;

          vDirection = modelPosition - vOrigin;

          gl_Position = czm_modelViewProjectionRelativeToEye * p;
      }`;

const fragmentShader = /* glsl */ `
      precision highp float;

      in vec3 vOrigin;
      in vec3 vDirection;

      // Volumetric cloud parameters
      uniform vec3 base;
      uniform float threshold;
      uniform float range;
      uniform float opacity;
      uniform float steps;
      uniform float frame;
      uniform vec3 boxDimensions;

      ${glslNoiseFunction}

      vec2 hitBox( vec3 orig, vec3 dir ) {
          const vec3 box_min = vec3( - 0.5 );
          const vec3 box_max = vec3( 0.5 );
          vec3 inv_dir = 1.0 / dir;
          vec3 tmin_tmp = ( box_min - orig ) * inv_dir;
          vec3 tmax_tmp = ( box_max - orig ) * inv_dir;
          vec3 tmin = min( tmin_tmp, tmax_tmp );
          vec3 tmax = max( tmin_tmp, tmax_tmp );
          float t0 = max( tmin.x, max( tmin.y, tmin.z ) );
          float t1 = min( tmax.x, min( tmax.y, tmax.z ) );
          return vec2( t0, t1 );
      }

      float sampleDensity( vec3 p ) {
          // Create cloud density using procedural noise
          // p is in box-local space (-0.5 to 0.5)

          // Scale sampling coordinates by box dimensions to get consistent noise frequency
          // in physical space rather than local box space
          vec3 physicalScale = boxDimensions / max(max(boxDimensions.x, boxDimensions.y), boxDimensions.z);

          // Apply physical scaling to noise sampling
          vec3 offset = vec3(100.0, 100.0, 100.0);
          vec3 samplePos = (p * physicalScale * 3.0 + offset);

          // Multi-octave noise for cloud-like appearance
          float density = fbm(samplePos);

          // Normalize from [-1, 1] to [0, 1]
          density = density * 0.5 + 0.5;

          // Add variation with another noise layer
          float detail = snoise(samplePos * 2.0) * 0.5 + 0.5;
          density = mix(density, density * detail, 0.3);

          // Radial falloff from center
          // Height-based falloff (Z is vertical)
          float heightFalloff = 1.0 - smoothstep(0.4, 0.5, abs(p.z));

          // Horizontal distance falloff (X, Y are horizontal)
          vec2 horizontalDist = vec2(p.x, p.y);
          float horizontalDistance = length(horizontalDist);
          float radialFalloff = 1.0 - smoothstep(0.45, 0.5, horizontalDistance);

          // Combine falloffs
          float edgeFalloff = heightFalloff * radialFalloff;
          density *= edgeFalloff;

          return max(0.0, density);
      }

      float shading( vec3 coord ) {
          float stepSize = 0.01;
          return sampleDensity( coord + vec3( - stepSize ) ) - sampleDensity( coord + vec3( stepSize ) );
      }

      vec4 linearToSRGB( in vec4 value ) {
          return vec4( mix( pow( value.rgb, vec3( 0.41666 ) ) * 1.055 - vec3( 0.055 ), value.rgb * 12.92, vec3( lessThanEqual( value.rgb, vec3( 0.0031308 ) ) ) ), value.a );
      }

      void main() {
          // vDirection is already (modelPosition - vOrigin), just normalize it
          vec3 rayDir = normalize( vDirection );

          vec2 bounds = hitBox( vOrigin, rayDir );
          if ( bounds.x > bounds.y ) discard;
          bounds.x = max( bounds.x, 0.0 );

          vec3 p = vOrigin + bounds.x * rayDir;
          vec3 inc = 1.0 / abs( rayDir );
          float delta = min( inc.x, min( inc.y, inc.z ) );
          delta /= steps;

          vec4 ac = vec4( base, 0.0 );

          for ( float t = bounds.x; t < bounds.y; t += delta ) {
              float d = sampleDensity( p );
              d = smoothstep( threshold - range, threshold + range, d ) * opacity;
              float col = shading( p ) * 3.0 + ( ( p.x + p.y ) * 0.25 ) + 0.2;
              ac.rgb += ( 1.0 - ac.a ) * d * col;
              ac.a += ( 1.0 - ac.a ) * d;
              if ( ac.a >= 0.95 ) break;
              p += rayDir * delta;
          }

          vec4 color = linearToSRGB( ac );
          color = czm_gammaCorrect( color );
          if ( color.a == 0.0 ) discard;
          out_FragColor = color;
      }
`;

/**
 * Initialize volumetric clouds over Liège, Belgium
 * @param {Cesium.Viewer} viewer - The Cesium viewer instance
 * @param {Object} params - Cloud parameters
 * @param {number} params.opacity - Cloud opacity (0-1)
 * @param {number} params.threshold - Density threshold (0-1)
 * @param {number} params.range - Density range (0-1)
 * @param {number} params.steps - Raymarching steps (20-200)
 * @returns {Object} Cloud primitive and uniforms
 */
export function initializeClouds(viewer, params = {}) {
  // Default parameters
  const defaultParams = {
    opacity: 0.25,
    threshold: 0.4,
    range: 0.15,
    steps: 80,
  };

  const cloudParams = { ...defaultParams, ...params };

  // Liège extent from existing app data
  // West: 5.5724°E, South: 50.6103°N, East: 5.6646°E, North: 50.6560°N
  const liegeCenter = Cesium.Cartesian3.fromDegrees(
    5.6185, // Center longitude
    50.6332, // Center latitude
    // 5000,    // Altitude: 5000m (raised to avoid ground clipping)
    2000,
  );

  const modelMatrix = Cesium.Transforms.eastNorthUpToFixedFrame(liegeCenter);

  // Create a flatter, wider cloud layer in meters
  const cloudWidth = 15000;   // 15km wide (east-west, X axis)
  const cloudHeight = 1650;   // 1.65km tall (vertical, should be Y axis)
  const cloudDepth = 15000;   // 15km deep (north-south, Z axis)

  const renderState = Cesium.RenderState.fromCache({
    depthMask: false,
    blending: {
      enabled: true,
      color: {
        red: 0.0,
        green: 0.0,
        blue: 0.0,
        alpha: 0.0,
      },
    },
    depthTest: {
      enabled: true,
      func: Cesium.DepthFunction.LESS_OR_EQUAL,
    },
    cull: {
      enabled: false, // Disable culling to see clouds from any angle
    },
  });

  // Apply non-uniform scale to create the flatter cloud layer
  // Swap Y and Z if needed based on coordinate system
  const scaleMat = Cesium.Matrix4.fromScale(
    new Cesium.Cartesian3(cloudWidth, cloudDepth, cloudHeight),
    new Cesium.Matrix4(),
  );

  Cesium.Matrix4.multiply(modelMatrix, scaleMat, modelMatrix);

  // Create a unit box (-0.5 to 0.5) that will be scaled by the matrix
  const halfBox = new Cesium.Cartesian3(0.5, 0.5, 0.5);
  const negHalfBox = new Cesium.Cartesian3(-0.5, -0.5, -0.5);

  const boxGeometry = new Cesium.BoxGeometry({
    minimum: negHalfBox,
    maximum: halfBox,
  });

  const uniforms = {
    base: new Cesium.Color(0.45, 0.45, 0.45, 0),  // Neutral grey for overcast clouds
    opacity: cloudParams.opacity,
    range: cloudParams.range,
    steps: cloudParams.steps,
    frame: 0,
    threshold: cloudParams.threshold,
    boxDimensions: new Cesium.Cartesian3(cloudWidth, cloudDepth, cloudHeight),
  };

  const cmdUniforms = {};
  for (const key in uniforms) {
    if (key) {
      cmdUniforms[key] = function () {
        return uniforms[key];
      };
    }
  }

  const primitive = new GeometryPrimitive(boxGeometry, {
    uniformMap: cmdUniforms,
    vertexShaderSource: vertexShader,
    fragmentShaderSource: fragmentShader,
    renderState: renderState,
    modelMatrix,
    pass: Cesium.Pass.TRANSLUCENT,
  });

  return {
    primitive,
    uniforms,
    centerPoint: liegeCenter,
  };
}

/**
 * Update cloud parameters
 * @param {Object} uniforms - The cloud uniforms object
 * @param {Object} params - Parameters to update
 */
export function updateCloudParameters(uniforms, params) {
  if (params.opacity !== undefined) {
    uniforms.opacity = params.opacity;
  }
  if (params.threshold !== undefined) {
    uniforms.threshold = params.threshold;
  }
  if (params.range !== undefined) {
    uniforms.range = params.range;
  }
  if (params.steps !== undefined) {
    uniforms.steps = params.steps;
  }
}

/**
 * Fly camera to view clouds over Liège
 * @param {Cesium.Viewer} viewer - The Cesium viewer instance
 * @param {Cesium.Cartesian3} centerPoint - Cloud center point
 */
export function flyToCloudView(viewer, centerPoint) {
  viewer.camera.flyTo({
    destination: centerPoint,
    orientation: {
      heading: Cesium.Math.toRadians(45),
      pitch: Cesium.Math.toRadians(-35),
      roll: 0,
    },
    duration: 2,
  });
}
